import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '../App';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Building2, Users, Car, DollarSign, Clock, Plus, Copy, 
  RefreshCw, UserPlus, Crown, Shield, User, Trash2, 
  Settings, Mail, ChevronRight, Search, Check, X, MessageSquare,
  Globe, Lock, Eye
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';

export default function CompanyDashboard() {
  const navigate = useNavigate();
  const { user, refreshUser } = useContext(AuthContext);
  const [company, setCompany] = useState(null);
  const [members, setMembers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showJoinDialog, setShowJoinDialog] = useState(false);
  const [showDiscoverDialog, setShowDiscoverDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('driver');
  const [inviteCode, setInviteCode] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [joinRequests, setJoinRequests] = useState([]);
  const [myJoinRequests, setMyJoinRequests] = useState([]);
  const [discoverCompanies, setDiscoverCompanies] = useState([]);
  const [discoverSearch, setDiscoverSearch] = useState('');
  const [joinMessage, setJoinMessage] = useState('');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: '',
    description: '',
    phone: '',
    address: '',
    website: ''
  });

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      
      // Check if user has a company
      const companyRes = await axios.get(`${API}/companies/my-company`);
      
      if (companyRes.data) {
        setCompany(companyRes.data);
        
        // Fetch dashboard data
        const dashRes = await axios.get(`${API}/companies/${companyRes.data.id}/dashboard`);
        setMembers(dashRes.data.members);
        setStats(dashRes.data.stats);
        
        // Get invite code if owner/manager
        if (user?.company_role === 'owner' || user?.company_role === 'manager') {
          try {
            const codeRes = await axios.get(`${API}/companies/${companyRes.data.id}/invite-code`);
            setInviteCode(codeRes.data.invite_code);
            
            // Fetch pending join requests
            const requestsRes = await axios.get(`${API}/companies/${companyRes.data.id}/join-requests`);
            setJoinRequests(requestsRes.data.requests || []);
          } catch (e) {
            console.error('Error fetching invite code or join requests:', e);
          }
        }
      } else {
        // User doesn't have a company - fetch their pending join requests
        try {
          const myRequestsRes = await axios.get(`${API}/companies/my-join-requests`);
          setMyJoinRequests(myRequestsRes.data.requests || []);
        } catch (e) {
          console.error('Error fetching my join requests:', e);
        }
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiscoverCompanies = async () => {
    try {
      const res = await axios.get(`${API}/companies/discover`, {
        params: discoverSearch ? { search: discoverSearch } : {}
      });
      setDiscoverCompanies(res.data.companies || []);
    } catch (error) {
      console.error('Error discovering companies:', error);
    }
  };

  useEffect(() => {
    if (showDiscoverDialog) {
      fetchDiscoverCompanies();
    }
  }, [showDiscoverDialog, discoverSearch]);

  const handleCreateCompany = async () => {
    if (!createForm.name.trim()) {
      toast.error('Company name is required');
      return;
    }

    try {
      await axios.post(`${API}/companies`, createForm);
      toast.success('Company created successfully!');
      setShowCreateDialog(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create company');
    }
  };

  const handleJoinCompany = async () => {
    if (!joinCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    try {
      await axios.post(`${API}/companies/join/${joinCode.trim().toUpperCase()}`);
      toast.success('Successfully joined the company!');
      setShowJoinDialog(false);
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid invite code');
    }
  };

  const handleRequestToJoin = async (companyId) => {
    try {
      await axios.post(`${API}/companies/${companyId}/join-request`, {
        company_id: companyId,
        message: joinMessage
      });
      toast.success('Join request sent!');
      setJoinMessage('');
      setSelectedCompany(null);
      fetchDiscoverCompanies();
      
      // Refresh my join requests
      const myRequestsRes = await axios.get(`${API}/companies/my-join-requests`);
      setMyJoinRequests(myRequestsRes.data.requests || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send join request');
    }
  };

  const handleCancelJoinRequest = async (requestId) => {
    try {
      await axios.delete(`${API}/companies/join-requests/${requestId}`);
      toast.success('Join request cancelled');
      const myRequestsRes = await axios.get(`${API}/companies/my-join-requests`);
      setMyJoinRequests(myRequestsRes.data.requests || []);
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/companies/${company.id}/join-requests/${requestId}`, {
        action: 'approve'
      });
      toast.success('Join request approved!');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requestId) => {
    try {
      await axios.put(`${API}/companies/${company.id}/join-requests/${requestId}`, {
        action: 'reject'
      });
      toast.success('Join request rejected');
      setJoinRequests(joinRequests.filter(r => r.id !== requestId));
    } catch (error) {
      toast.error('Failed to reject request');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }

    try {
      await axios.post(`${API}/companies/${company.id}/members`, {
        email: inviteEmail.trim().toLowerCase(),
        role: inviteRole
      });
      toast.success('Member added successfully!');
      setShowInviteDialog(false);
      setInviteEmail('');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from the company?`)) return;

    try {
      await axios.delete(`${API}/companies/${company.id}/members/${userId}`);
      toast.success('Member removed');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/companies/${company.id}/members/${userId}`, { role: newRole });
      toast.success('Role updated');
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRegenerateCode = async () => {
    if (!window.confirm('Regenerate invite code? The old code will stop working.')) return;

    try {
      const res = await axios.post(`${API}/companies/${company.id}/regenerate-invite-code`);
      setInviteCode(res.data.invite_code);
      toast.success('Invite code regenerated');
    } catch (error) {
      toast.error('Failed to regenerate code');
    }
  };

  const copyInviteCode = () => {
    navigator.clipboard.writeText(inviteCode);
    toast.success('Invite code copied!');
  };

  const handleLeaveCompany = async () => {
    if (!window.confirm('Are you sure you want to leave this company?')) return;

    try {
      await axios.post(`${API}/companies/${company.id}/leave`);
      toast.success('You have left the company');
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave company');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-gold-500" />;
      case 'manager': return <Shield className="w-4 h-4 text-blue-500" />;
      default: return <User className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // No company - show create/join/discover options
  if (!company) {
    return (
      <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
        <div className="text-center py-8">
          <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Company Groups</h1>
          <p className="text-gray-500 mb-8">
            Create your own limo company or join an existing one
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button size="lg" data-testid="create-company-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Create Company
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create Your Company</DialogTitle>
                  <DialogDescription>
                    Set up your limo company and start adding drivers
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      value={createForm.name}
                      onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="e.g., Elite Limo Services"
                      data-testid="company-name-input"
                    />
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input
                      value={createForm.description}
                      onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                      placeholder="Brief description of your company"
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={createForm.phone}
                      onChange={(e) => setCreateForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input
                      value={createForm.address}
                      onChange={(e) => setCreateForm(f => ({ ...f, address: e.target.value }))}
                      placeholder="123 Main St, Miami, FL"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                  <Button onClick={handleCreateCompany} data-testid="confirm-create-btn">Create Company</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={showJoinDialog} onOpenChange={setShowJoinDialog}>
              <DialogTrigger asChild>
                <Button size="lg" variant="outline" data-testid="join-company-btn">
                  <UserPlus className="w-5 h-5 mr-2" />
                  Join with Code
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Join a Company</DialogTitle>
                  <DialogDescription>
                    Enter the invite code from your company owner
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <Label>Invite Code</Label>
                  <Input
                    value={joinCode}
                    onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                    placeholder="ABCD1234"
                    className="text-center text-2xl tracking-widest"
                    maxLength={8}
                    data-testid="join-code-input"
                  />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowJoinDialog(false)}>Cancel</Button>
                  <Button onClick={handleJoinCompany} data-testid="confirm-join-btn">Join Company</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={showDiscoverDialog} onOpenChange={setShowDiscoverDialog}>
              <DialogTrigger asChild>
                <Button size="lg" variant="secondary" data-testid="discover-companies-btn">
                  <Search className="w-5 h-5 mr-2" />
                  Discover Companies
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[80vh]">
                <DialogHeader>
                  <DialogTitle>Discover Companies</DialogTitle>
                  <DialogDescription>
                    Find and request to join limo companies
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      placeholder="Search companies..."
                      value={discoverSearch}
                      onChange={(e) => setDiscoverSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="max-h-96 overflow-y-auto space-y-3">
                    {discoverCompanies.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No companies found</p>
                    ) : (
                      discoverCompanies.map((c) => (
                        <Card key={c.id} className="hover:shadow-md transition-shadow">
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-semibold">{c.name}</h4>
                                <p className="text-sm text-gray-500">{c.description || 'No description'}</p>
                                <p className="text-xs text-gray-400 mt-1">{c.member_count} members</p>
                              </div>
                              <div>
                                {c.has_pending_request ? (
                                  <Badge variant="secondary">Request Pending</Badge>
                                ) : selectedCompany === c.id ? (
                                  <div className="space-y-2 w-64">
                                    <Textarea
                                      placeholder="Optional message to the owner..."
                                      value={joinMessage}
                                      onChange={(e) => setJoinMessage(e.target.value)}
                                      className="text-sm"
                                      rows={2}
                                    />
                                    <div className="flex gap-2">
                                      <Button size="sm" onClick={() => handleRequestToJoin(c.id)}>
                                        Send Request
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => setSelectedCompany(null)}>
                                        Cancel
                                      </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <Button size="sm" onClick={() => setSelectedCompany(c.id)}>
                                    Request to Join
                                  </Button>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* My Pending Join Requests */}
        {myJoinRequests.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                My Join Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {myJoinRequests.map((req) => (
                <div 
                  key={req.id} 
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{req.company_name}</p>
                    <p className="text-sm text-gray-500">
                      Requested {new Date(req.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={
                      req.status === 'pending' ? 'secondary' :
                      req.status === 'approved' ? 'default' : 'destructive'
                    }>
                      {req.status}
                    </Badge>
                    {req.status === 'pending' && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-red-500"
                        onClick={() => handleCancelJoinRequest(req.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  const isOwner = user?.company_role === 'owner';
  const isManager = user?.company_role === 'manager';
  const canManage = isOwner || isManager;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building2 className="w-6 h-6" />
            {company.name}
          </h1>
          <p className="text-gray-500">{company.description || 'Your private limo company'}</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
              <DialogTrigger asChild>
                <Button data-testid="add-member-btn">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Team Member</DialogTitle>
                  <DialogDescription>
                    Add a driver or manager to your company
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div>
                    <Label>Email Address</Label>
                    <Input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="driver@example.com"
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div>
                    <Label>Role</Label>
                    <Select value={inviteRole} onValueChange={setInviteRole}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="driver">Driver</SelectItem>
                        {isOwner && <SelectItem value="manager">Manager</SelectItem>}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                  <Button onClick={handleInviteMember}>Add Member</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            <Button variant="outline" onClick={() => fetchDashboard()}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_members}</p>
                  <p className="text-sm text-gray-500">Members</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <Car className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_rides}</p>
                  <p className="text-sm text-gray-500">Total Rides</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${stats.total_revenue?.toLocaleString()}</p>
                  <p className="text-sm text-gray-500">Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gold-100 rounded-lg">
                  <Clock className="w-5 h-5 text-gold-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_event_hours?.toFixed(1)}h</p>
                  <p className="text-sm text-gray-500">Event Hours</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="members" className="w-full">
        <TabsList>
          <TabsTrigger value="members">
            Members ({members.length})
          </TabsTrigger>
          {canManage && (
            <TabsTrigger value="requests">
              Join Requests {joinRequests.length > 0 && `(${joinRequests.length})`}
            </TabsTrigger>
          )}
          {canManage && (
            <TabsTrigger value="settings">
              Settings
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="members" className="mt-4">
          {/* Members List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5" />
                Team Members
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {members.map((member) => (
                  <div 
                    key={member.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {getRoleIcon(member.role)}
                      <div>
                        <p className="font-medium">{member.user_name}</p>
                        <p className="text-sm text-gray-500">{member.user_email}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${
                        member.role === 'owner' ? 'bg-gold-100 text-gold-700' :
                        member.role === 'manager' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {member.role}
                      </span>
                      {canManage && member.role !== 'owner' && member.user_id !== user?.id && (
                        <>
                          {isOwner && (
                            <Select 
                              value={member.role} 
                              onValueChange={(value) => handleUpdateRole(member.user_id, value)}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="driver">Driver</SelectItem>
                                <SelectItem value="manager">Manager</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleRemoveMember(member.user_id, member.user_name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {canManage && (
          <TabsContent value="requests" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Pending Join Requests
                </CardTitle>
                <CardDescription>
                  Review requests from drivers who want to join your company
                </CardDescription>
              </CardHeader>
              <CardContent>
                {joinRequests.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No pending join requests</p>
                ) : (
                  <div className="space-y-3">
                    {joinRequests.map((req) => (
                      <div 
                        key={req.id} 
                        className="flex items-start justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <div>
                          <p className="font-medium">{req.user_name}</p>
                          <p className="text-sm text-gray-500">{req.user_email}</p>
                          {req.message && (
                            <p className="text-sm text-gray-600 mt-2 italic">"{req.message}"</p>
                          )}
                          <p className="text-xs text-gray-400 mt-1">
                            Requested {new Date(req.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleApproveRequest(req.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            className="text-red-500 border-red-200 hover:bg-red-50"
                            onClick={() => handleRejectRequest(req.id)}
                          >
                            <X className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {canManage && (
          <TabsContent value="settings" className="mt-4 space-y-4">
            {/* Invite Code Section */}
            {inviteCode && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invite Code</CardTitle>
                  <CardDescription>Share this code with drivers to join your company instantly</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center gap-4">
                    <div className="text-3xl font-mono font-bold tracking-widest bg-gray-100 dark:bg-gray-800 px-6 py-3 rounded-lg">
                      {inviteCode}
                    </div>
                    <Button variant="outline" onClick={copyInviteCode}>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy
                    </Button>
                    {isOwner && (
                      <Button variant="ghost" onClick={handleRegenerateCode}>
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Regenerate
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Visibility Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Content Visibility
                </CardTitle>
                <CardDescription>Control what content is visible to others</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Lock className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Company Only (Default)</p>
                    <p className="text-sm text-gray-500">Rides and events are only visible to your company members</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <Globe className="w-5 h-5 text-blue-500 mt-0.5" />
                  <div>
                    <p className="font-medium">Share to Platform</p>
                    <p className="text-sm text-gray-500">
                      As the company owner, you can share specific rides or events to be visible to all platform users.
                      Look for the "Share to Platform" option on ride and event details.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Leave Company (for non-owners) */}
      {!isOwner && (
        <div className="text-center pt-4">
          <Button variant="ghost" className="text-red-500" onClick={handleLeaveCompany}>
            Leave Company
          </Button>
        </div>
      )}
    </div>
  );
}
